import React from "react";
import { MathJax } from "better-react-mathjax";

export function QuadratureIntro({ lite = false }) {
  const baseClass = lite
    ? 'space-y-2 text-justify hyphens-auto text-sm leading-relaxed text-white/80 md:text-base'
    : 'space-y-4 text-justify hyphens-auto leading-relaxed text-white/85 text-sm md:text-base';
  return (
    <div className={baseClass}>
      Numerical quadrature (numerical integration) approximates 
      integrals <MathJax inline>{"\\( I=\\int_a^b f(x)\\,dx \\)"}</MathJax> from finitely many 
      evaluations of <MathJax inline>{"\\( f \\)"}</MathJax>. It is fundamental in scientific computing whenever analytic
      antiderivatives are unavailable, expensive to evaluate, or when data arrive as samples rather than
      closed forms. Classical Newton–Cotes rules—box/rectangle, trapezoid, and Simpson—arise by integrating
      local polynomial interpolants exactly on each panel. Composite (summed) rules extend these local
      formulas across a partition of <MathJax inline>{"\\( [a,b] \\)"}</MathJax>, yielding predictable global error
      behavior as the mesh is refined. Careful step selection, extrapolation, and adaptivity further enhance
      accuracy for smooth functions or concentrate work near regions of rapid variation.
    </div>
  );
}

export default function QuadratureLongExplanation() {
  return (
    <div className="space-y-6 text-sm leading-relaxed text-white/80 text-justify hyphens-auto md:text-base">
      <h2 className="text-sm font-semibold md:text-base">1. Setup & Motivation</h2>
      <p>
        The overall Goal is to approximate <MathJax inline>{"\\( I=\\int_a^b f(x)\\,dx \\)"}</MathJax> given samples of{" "}
        <MathJax inline>{"\\( f \\)"}</MathJax>. Applications include deterministic modeling, uncertainty
        quantification, and data-driven integration where only pointwise access to <MathJax inline>{"\\( f \\)"}</MathJax> is available.
        Accuracy depends on the smoothness of <MathJax inline>{"\\( f \\)"}</MathJax>, the placement of nodes, and the order of the rule.
        Efficient quadrature balances the number of function calls (often the dominant cost) against the desired tolerance.
      </p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Physics/engineering:</strong> forces, fluxes, and work integrals computed from experimental or simulated data.</li>
        <li><strong>Probabilities:</strong> expectations <MathJax inline>{"\\( \\mathbb{E}[g(X)] \\)"}</MathJax> and CDFs via density integrals.</li>
        <li><strong>Computational science:</strong> post-processing PDE solutions; evaluating weak forms and residuals.</li>
        <li><strong>Data analytics:</strong> area-under-curve metrics (AUC/ROC), smoothing, and resampling pipelines.</li>
      </ul>

      <h2 className="text-sm font-semibold mt-4 md:text-base">2. From Interpolation to Quadrature</h2>
      <p>
        Over a small interval <MathJax inline>{"\\( [x_i,x_{i+1}] \\)"}</MathJax> of 
        width{" "} <MathJax inline>{"\\( h=x_{i+1}-x_i \\)"}</MathJax>, approximate <MathJax inline>{"\\( f \\)"}</MathJax> by a low-degree
        polynomial interpolant (constant, linear, or quadratic) through available samples, and integrate that interpolant
        exactly. The resulting panel rule inherits exactness for polynomials up to the degree of the interpolant and
        admits a truncation error proportional to a higher derivative of <MathJax inline>{"\\( f \\)"}</MathJax>. Summing across
        the mesh yields composite rules with global error characterized by a power of <MathJax inline>{"\\( h \\)"}</MathJax>.
      </p>

      <h2 className="text-sm font-semibold mt-4 md:text-base">3. Box (Rectangle) Rules</h2>
      <p>
        For uniform nodes <MathJax inline>{"\\( x_i=a+ih \\)"}</MathJax> with <MathJax inline>{"\\( h=(b-a)/n \\)"}</MathJax>:
      </p>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Left box:</strong> <MathJax inline>{"\\( Q_{\\text{L}}=h\\sum_{i=0}^{n-1} f(x_i) \\)"}</MathJax></li>
        <li><strong>Right box:</strong> <MathJax inline>{"\\( Q_{\\text{R}}=h\\sum_{i=1}^{n} f(x_i) \\)"}</MathJax></li>
        <li><strong>Midpoint (centered, open NC of degree 1):</strong> <MathJax inline>{"\\( Q_{\\text{M}}=h\\sum_{i=0}^{n-1} f\\big(x_i+\\tfrac{h}{2}\\big) \\)"}</MathJax></li>
      </ul>
      <p>
        <strong>Accuracy:</strong> Left/right are first order: 
        global error <MathJax inline>{"\\( \\mathcal{O}(h) \\)"}</MathJax>. Midpoint is second order: 
        global error <MathJax inline>{"\\( \\mathcal{O}(h^2) \\)"}</MathJax>. Midpoint’s symmetry cancels the first-order term, explaining its
        higher accuracy at nearly identical cost. For monotone <MathJax inline>{"\\( f \\)"}</MathJax>, left/right provide
        predictable over-/under-estimates that can be used to bracket the integral.
      </p>

      <h2 className="text-sm font-semibold mt-4 md:text-base">4. Trapezoid Rule</h2>
      <p>
        Linear interpolation on each panel yields the composite trapezoid rule
      </p>
      <MathJax>{String.raw`\[
        Q_T
        = h\Big(\tfrac12 f(a) + \sum_{i=1}^{n-1} f(x_i) + \tfrac12 f(b)\Big).
      \]`}</MathJax>
      <p>
        <strong>Local error (single panel):</strong>{" "} <MathJax inline>{"\\( E_T = -\\tfrac{h^3}{12} f''(\\xi) \\)"}</MathJax>. <strong>Global error:</strong>{" "} <MathJax inline>{"\\( I-Q_T = -\\tfrac{(b-a)}{12} f''(\\xi)\\, h^2 = \\mathcal{O}(h^2) \\)"}</MathJax>.
        Exactness holds for polynomials of degree ≤ 1. On periodic or analytic functions with matched endpoints,
        composite trapezoid can converge extraordinarily fast due to cancellation of high-frequency errors.
      </p>

      <h2 className="text-sm font-semibold mt-4 md:text-base">5. Simpson’s Rule</h2>
      <p>
        Quadratic interpolation over two panels <MathJax inline>{"\\( [x_{2k},x_{2k+2}] \\)"}</MathJax> with step{" "}
        <MathJax inline>{"\\( h \\)"}</MathJax> gives the <em>single</em> Simpson panel:
      </p>
      <MathJax>{String.raw`\[
        Q_S^{(2h)} = \frac{h}{3}\Big( f(x_{2k}) + 4f(x_{2k+1}) + f(x_{2k+2}) \Big).
      \]`}</MathJax>
      <p>
        The <em>composite</em> Simpson rule (requires even <MathJax inline>{"\\( n \\)"}</MathJax>):
      </p>
      <MathJax>{String.raw`\[
      \begin{aligned}
        Q_S = \frac{h}{3}\Big( f(a)+f(b)+&4\!\!\sum_{i\ \text{odd}} f(x_i)\\
        +&2\!\!\sum_{i\ \text{even},\,1\le i\le n-1} f(x_i) \Big).
        \end{aligned}
      \]`}</MathJax>
      <p>
        <strong>Local error (two-panel):</strong>{" "} <MathJax inline>{"\\( E_S = -\\tfrac{h^5}{90} f^{(4)}(\\xi) \\)"}</MathJax>; <strong>Global error:</strong>{" "} <MathJax inline>{"\\( I-Q_S = -\\tfrac{(b-a)}{180} f^{(4)}(\\xi)\\, h^4 = \\mathcal{O}(h^4) \\)"}</MathJax>.
        Exact for polynomials up to degree 3. Simpson is a strong default for smooth integrands on moderate meshes.
      </p>

      <h2 className="text-sm font-semibold mt-4 md:text-base">6. Local Truncation Error Derivations (Sketch)</h2>
      <ul className="list-disc pl-6 space-y-1">
        <li>
          <strong>Midpoint:</strong> A Taylor expansion on a symmetric interval shows
          <MathJax inline>{"\\( \\int_{x-h/2}^{x+h/2} f = h f(x) + \\tfrac{h^3}{24} f''(\\xi) \\)"}</MathJax>, so the leading
          error term involves <MathJax inline>{"\\( f'' \\)"}</MathJax>.
        </li>
        <li>
          <strong>Trapezoid:</strong> Averaging endpoint values gives
          <MathJax inline>{"\\( \\int_{x}^{x+h} f = \\tfrac{h}{2}[f(x)+f(x+h)] - \\tfrac{h^3}{12} f''(\\xi) \\)"}</MathJax>.
        </li>
        <li>
          <strong>Simpson:</strong> Integrating the quadratic interpolant through
          <MathJax inline>{"\\( x, x+h, x+2h \\)"}</MathJax> produces the <MathJax inline>{"\\( h^5 \\)"}</MathJax>-scale remainder involving
          <MathJax inline>{"\\( f^{(4)} \\)"}</MathJax>.
        </li>
      </ul>

      <h2 className="text-sm font-semibold mt-4 md:text-base">7. Degree of Precision & Exactness</h2>
      <p>
        A rule has degree of precision <MathJax inline>{"\\( m \\)"}</MathJax> if it integrates exactly all polynomials 
        of degree ≤ <MathJax inline>{"\\( m \\)"}</MathJax>. This captures the idea that the rule is “as good as” integrating the local
        interpolant. For smooth <MathJax inline>{"\\( f \\)"}</MathJax>, higher degree typically correlates with higher order,
        though node placement and symmetry also play key roles.
      </p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Midpoint & trapezoid: degree 1.</li>
        <li>Simpson: degree 3.</li>
      </ul>
      <p>
        Exactness provides quick diagnostics: if <MathJax inline>{"\\( f \\)"}</MathJax> is (or is well-approximated by) a low-degree polynomial,
        the corresponding rule can be extremely accurate even on coarse meshes.
      </p>

      <h2 className="text-sm font-semibold mt-4 md:text-base">8. Composite (Summed) Rules</h2>
      <p>
        Partition <MathJax inline>{"\\( [a,b] \\)"}</MathJax> into <MathJax inline>{"\\( n \\)"}</MathJax> panels of width{" "}
        <MathJax inline>{"\\( h \\)"}</MathJax>, apply a basic rule to each panel, and sum to obtain a global approximation.
        For sufficiently smooth <MathJax inline>{"\\( f \\)"}</MathJax>, composite errors scale like <MathJax inline>{"\\( \\mathcal{O}(h^p) \\)"}</MathJax> with orders:
      </p>
      <ul className="list-disc pl-6 space-y-1">
        <li>Left/Right box: <MathJax inline>{"\\( p=1 \\)"}</MathJax></li>
        <li>Midpoint & trapezoid: <MathJax inline>{"\\( p=2 \\)"}</MathJax></li>
        <li>Simpson: <MathJax inline>{"\\( p=4 \\)"}</MathJax> (with even <MathJax inline>{"\\( n \\)"}</MathJax>)</li>
      </ul>
      <p>
        In practice, composite rules allow reuse of function values between refinements (step-doubling), enabling
        cost-effective accuracy improvement and error estimation.
      </p>

      <h2 className="text-sm font-semibold mt-4 md:text-base">9. Error Constants (Global)</h2>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Midpoint (composite):</strong> <MathJax inline>{"\\( I-Q_M = \\tfrac{(b-a)}{24} f''(\\xi)\\, h^2 \\)"}</MathJax>.</li>
        <li><strong>Trapezoid (composite):</strong> <MathJax inline>{"\\( I-Q_T = -\\tfrac{(b-a)}{12} f''(\\xi)\\, h^2 \\)"}</MathJax>.</li>
        <li><strong>Simpson (composite):</strong> <MathJax inline>{"\\( I-Q_S = -\\tfrac{(b-a)}{180} f^{(4)}(\\xi)\\, h^4 \\)"}</MathJax>.</li>
      </ul>
      <p>
        Here <MathJax inline>{"\\( \\xi \\)"}</MathJax> lies in <MathJax inline>{"\\( (a,b) \\)"}</MathJax> and depends on
        <MathJax inline>{"\\( f \\)"}</MathJax> and the mesh. These constants explain observed convergence rates under mesh refinement.
      </p>

      <h2 className="text-sm font-semibold mt-4 md:text-base">10. Richardson Extrapolation & Romberg</h2>
      <p>
        If a sequence <MathJax inline>{"\\( Q(h) = I + C h^p + \\mathcal{O}(h^{p+1}) \\)"}</MathJax> is available (e.g., trapezoid with
        <MathJax inline>{"\\( p=2 \\)"}</MathJax>), one can eliminate the leading error term by combining two resolutions:
      </p>
      <MathJax>{String.raw`\[
        \hat I = \frac{2^p Q(h/2) - Q(h)}{2^p - 1}.
      \]`}</MathJax>
      <p>
        Iterating this construction on the trapezoid sequence yields <em>Romberg integration</em>, a tableau method that often
        attains very high accuracy rapidly for smooth integrands. Because it reuses previously computed samples, the marginal cost
        per refinement is modest.
      </p>

      <h2 className="text-sm font-semibold mt-4 md:text-base">11. Adaptive Quadrature (Composite Simpson)</h2>
      <ol className="list-decimal pl-6 space-y-1">
        <li>Compute Simpson on <MathJax inline>{"\\( [a,b] \\)"}</MathJax> and separately on the two halves.</li>
        <li>Use the difference to estimate the local error (scaling like <MathJax inline>{"\\( h^4 \\)"}</MathJax>).</li>
        <li>Accept the panel if below tolerance; otherwise recurse on subintervals (with a depth or work cap).</li>
      </ol>
      <p>
        Adaptive strategies concentrate effort where <MathJax inline>{"\\( f \\)"}</MathJax> varies rapidly, near singularities or kinks,
        while keeping cost low in smooth regions. A global tolerance can be enforced by summing local error estimates.
      </p>

      <h2 className="text-sm font-semibold mt-4 md:text-base">12. Special Structures & Notes</h2>
      <ul className="list-disc pl-6 space-y-1">
        <li><strong>Periodic/analytic functions:</strong> Composite trapezoid on a uniform grid can converge spectrally fast when endpoints match periodically.</li>
        <li><strong>Non-smooth behavior:</strong> Discontinuities in <MathJax inline>{"\\( f' \\)"}</MathJax> or higher derivatives reduce the observed order; refine near kinks or split the interval.</li>
        <li><strong>Endpoint singularities:</strong> Variable transformations (e.g., algebraic maps) or graded meshes mitigate blow-ups; otherwise, specialized rules are required.</li>
        <li><strong>Cost model:</strong> Each composite pass uses <MathJax inline>{"\\( n+1 \\)"}</MathJax> endpoint samples (midpoints for midpoint rule); total work scales linearly with the number of evaluations.</li>
      </ul>

      <h2 className="text-sm font-semibold mt-4 md:text-base">13. Worked Examples</h2>
      <div className="space-y-3">
        <div>
          <p><strong>Example A (Exactness check):</strong> Integrate <MathJax inline>{"\\( f(x)=x^3 \\)"}</MathJax> on <MathJax inline>{"\\( [0,1] \\)"}</MathJax>.</p>
          <p>
            Exact value <MathJax inline>{"\\( I=\\tfrac{1}{4} \\)"}</MathJax>. Simpson is exact for any even <MathJax inline>{"\\( n \\)"}</MathJax> since its degree of precision is 3.
            Midpoint and trapezoid converge with second order; halving <MathJax inline>{"\\( h \\)"}</MathJax> reduces error by about a factor of 4.
          </p>
        </div>
        <div>
          <p><strong>Example B (Smooth non-polynomial):</strong> <MathJax inline>{"\\( I=\\int_0^1 e^{-x^2}\\,dx \\)"}</MathJax> (no elementary antiderivative).</p>
          <p>
            Composite Simpson with modest <MathJax inline>{"\\( n \\)"}</MathJax> rapidly approaches the 
            true value <MathJax inline>{"\\( \\approx 0.746824\\ldots \\)"}</MathJax>; halving <MathJax inline>{"\\( h \\)"}</MathJax> typically 
            reduces error by <MathJax inline>{"\\( 2^4=16 \\)"}</MathJax>, consistent with fourth-order behavior.
          </p>
        </div>
        <div>
          <p><strong>Example C (Periodic case):</strong> <MathJax inline>{"\\( I=\\int_0^{2\\pi} \\sin x\\,dx=0 \\)"}</MathJax>.</p>
          <p>
            Composite trapezoid on a uniform grid that includes both endpoints yields the exact result for any
            <MathJax inline>{"\\( n \\)"}</MathJax>, reflecting discrete orthogonality of Fourier modes on the grid.
          </p>
        </div>
      </div>
    </div>
  );
}
